import type { DashboardAggregations } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { gql, useQuery } from "@apollo/client";

const DASHBOARD_AGGREGATIONS_QUERY = gql`
  query GetDashboardAggregations(
    $tenantId: String!
    $startDate: String!
    $endDate: String!
    $granularity: String!
    $topLimit: Int
  ) {
    dashboardAggregations(
      tenantId: $tenantId
      startDate: $startDate
      endDate: $endDate
      granularity: $granularity
      topLimit: $topLimit
    ) {
      timeSeriesByAccount {
        period
        date
        totalIncome
        totalExpenses
        incomeByAccount {
          accountId
          accountName
          accountNumber
          amount
        }
        expensesByAccount {
          accountId
          accountName
          accountNumber
          amount
        }
      }
      timeSeriesByCounterparty {
        period
        date
        totalIncome
        totalExpenses
        incomeByCounterparty {
          entityId
          entityName
          amount
        }
        expensesByCounterparty {
          entityId
          entityName
          amount
        }
      }
      topCustomers {
        entityId
        entityName
        entityType
        totalAmount
      }
      topSuppliers {
        entityId
        entityName
        entityType
        totalAmount
      }
    }
  }
`;

export interface GetDashboardAggregationsParams {
  startDate?: Date;
  endDate?: Date;
  granularity?: "daily" | "weekly" | "monthly" | "quarterly";
  topLimit?: number;
}

export const useGetDashboardAggregations = ({
  startDate,
  endDate,
  granularity = "monthly",
  topLimit = 10,
}: GetDashboardAggregationsParams) => {
  const { tenantId } = useTenant();

  const { data, loading, error, refetch } = useQuery<{
    dashboardAggregations: DashboardAggregations;
  }>(DASHBOARD_AGGREGATIONS_QUERY, {
    variables: {
      tenantId,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      granularity,
      topLimit,
    },
    skip: !tenantId || !startDate || !endDate,
  });

  return {
    data: data?.dashboardAggregations || null,
    loading,
    error,
    refetch,
  };
};
